import {ActionIcon, Alert, Button, Paper, Switch, TextInput} from "@mantine/core";
import {FloppyDiskIcon, PlusIcon, TrashIcon} from "@phosphor-icons/react";
import {useCallback, useContext, useState} from "react";
import withRouteErrorBoundary from "../../components/ErrorBoundary/withRouteErrorBoundary";
import {Row, Col} from "../../components/Flex/Flex";
import Page from "../../components/Page/Page.tsx";
import {v4 as uuid} from 'uuid';
import MessageContext from "../../context/MessageContext.tsx";
import api, {useBHSystems, useSWCLocation} from "../../utils/api.ts";
import {sanitizeTargetName} from "../../utils/sanitize.ts";
import {AxiosResponse} from "axios";
import SearchSelect from "../../components/SearchSelect/SearchSelect.tsx";
import {System, UserLocation} from "../../types/swc.types.ts";


const getEmpty = () => ({uuid: uuid(), name: ''});
const emptyLocation = {system: null, galX: null, galY: null, planet: '', groundX: null, groundY: null};


interface TargetLocation {
    system: System | null,
    galX: number | null,
    galY: number | null,
    planet: string,
    groundX: number | null,
    groundY: number | null
}
interface Target {
    uuid: string,
    name: string,
}
interface TargetRowProps {
    target: Target,
    updateTarget: (uuid: string, key: string, value: any) => void,
    removeTarget: (uuid: string) => void,
    correctName: (uuid: string) => void
}

const getLocationSystem = (location: UserLocation | null, systems: System[]): TargetLocation => {
    if(location && typeof location !== 'undefined') {
        if(location.system.attributes.uid) {
            let system = systems.find(s => s.id === location.system.attributes.uid);
            if(system) {
                return {
                    system,
                    galX: system.galX,
                    galY: system.galY,
                    planet: '',
                    groundX: null,
                    groundY: null,
                }
            }
        }
    }
    return emptyLocation;
}

function ReportSightings () {
    const {success, error} = useContext(MessageContext);
    const {systems} = useBHSystems();
    const {location} = useSWCLocation();
    const [targetLocation, setTargetLocation] = useState<TargetLocation>(getLocationSystem(location, systems));
    const [anonymous, setAnonymous] = useState<boolean>(false);
    const [targets, setTargets] = useState<Target[]>([getEmpty()]);

    const updateTarget = useCallback((uuid: string, key: string, value: any) => {
        setTargets(prev => {
            let next = [...prev];
            let i = next.findIndex(t => t.uuid === uuid);
            next[i] = {...next[i]};
            // @ts-ignore
            next[i][key] = value;
            return next;
        })
    }, []);
    const removeTarget = useCallback((uuid: string) => {
        setTargets(prev => ([...prev]).filter(t => t.uuid !== uuid))
    }, []);

    const save = () => {
        if(!targetLocation.system) {
            error('Select a system');
            return;
        }
        api.post('/bh/reportSighting', {location: targetLocation, anonymous, targets}).then((res:AxiosResponse) => {
            success('Targets reported', `You have reported ${res.data.count} targets`);
            setTargets([getEmpty()]);
            setTargetLocation(emptyLocation);
        });
    }

    const onChooseSystem = useCallback((v: string | null) => {
        let system = systems.find(s => s.id === v);
        setTargetLocation(l => ({...l, system: (system ?? null)}));
    }, [systems]);

    const correctName = useCallback((uuid: string) => {
        setTargets(prev => {
            let next = [...prev];
            let i = next.findIndex(t => t.uuid === uuid);
            next[i] = {...next[i]};
            next[i].name = sanitizeTargetName(next[i].name);
            return next;
        });
    }, []);

    return (
        <Page>
            <title>Report Sighting</title>
            <Row mb={4} justify="center">
                <Alert color="blue" className="mx-auto">Reporting location is limited to system for now. An expansion to the planet and ground coordinates is planned</Alert>
            </Row>

            <Paper>
                <Row>
                    <Col grow={1}>
                        <h3 className="mt-0">Report Sighting</h3>
                    </Col>
                    <Col>
                        <Button leftSection={<FloppyDiskIcon size={16}/>} onClick={save}>Report targets</Button>
                    </Col>
                </Row>
                <Row align="end" mb={3}>
                    <Col basis="200px">
                        <SearchSelect label="Location" data={systems.map(s => ({value: s.id, label: s.name}))} value={targetLocation.system?.id || null} onChange={onChooseSystem} placeholder="System"/>
                    </Col>
                    <Col>
                        <Switch id="anonymous" checked={anonymous} onChange={e => setAnonymous(e.currentTarget.checked)} label="Anonymous" />
                    </Col>
                </Row>
                {targets.map(t => <TargetRow key={t.uuid} target={t} updateTarget={updateTarget} removeTarget={removeTarget} correctName={correctName} />)}
                <Row>
                    <Col grow={1}>
                        <Button leftSection={<PlusIcon size={16}/>} onClick={() => setTargets(prev => [...prev, getEmpty()])}>Add more targets</Button>
                    </Col>
                </Row>

            </Paper>
        </Page>
    )
}


const TargetRow = ({target, updateTarget, removeTarget, correctName}: TargetRowProps) => {
    return (
        <Row mb={2} align="center">
            <Col>
                <TextInput value={target.name} onChange={e => updateTarget(target.uuid, 'name', e.target.value)} placeholder="Target name" onBlur={() => correctName(target.uuid)} />
            </Col>
            <Col className="justify-self-end">
                <ActionIcon variant="light" radius="xl" onClick={() => removeTarget(target.uuid)}><TrashIcon size={16}/></ActionIcon>
            </Col>
        </Row>
    )
}


export default withRouteErrorBoundary(ReportSightings);
